import { Attributes, SpanStatusCode, trace, Tracer } from "@opentelemetry/api";

import CONV from "../semantic-conventions";
import { getCurrentSessionId, getPreviousSessionId } from "../sessions/store";
import { ErrorEventPayload, ErrorInstrumentationOptions, ErrorSource, RootSpan } from "../types";
import { forceError, log, warn } from "../utils/logger";
import { parseStack } from "../utils/stacktrace";

const DEFAULT_IGNORE_PATTERNS: (string | RegExp)[] = [
  /^Script error\.?$/i,
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /AbortError/i,
];

const DEFAULT_OPTIONS: Required<
  Pick<ErrorInstrumentationOptions, "console" | "global" | "report" | "network">
> = {
  console: true,
  global: true,
  report: true,
  network: true,
};

// Internal batching configuration (not user-configurable)
const BATCH_INTERVAL_MS = 30_000;
const BATCH_SIZE = 50;

const MAX_SERIALIZED_LENGTH = 4000;

type FlushReason = "manual" | "timer" | "size" | "view_end";

type ProcessOptions = {
  skipIgnore?: boolean;
  skipBeforeSend?: boolean;
};

export class ErrorCollector {
  private tracer: Tracer;

  private config: typeof DEFAULT_OPTIONS;

  private beforeSend?: ErrorInstrumentationOptions["beforeSend"];

  private ignorePatterns: (string | RegExp)[];

  private currentRootSpan: RootSpan = undefined;

  private currentViewId?: string;

  private listenersSet = false;

  private consolePatched = false;

  private queue: ErrorEventPayload[] = [];

  private flushTimer?: ReturnType<typeof setTimeout>;

  constructor(tracer: Tracer, options?: ErrorInstrumentationOptions) {
    this.tracer = tracer;
    this.config = { ...DEFAULT_OPTIONS };
    if (options) {
      (Object.keys(DEFAULT_OPTIONS) as Array<keyof typeof DEFAULT_OPTIONS>).forEach((key) => {
        const value = options[key];
        if (typeof value !== "undefined") {
          (this.config as any)[key] = value;
        }
      });
    }
    this.beforeSend = options?.beforeSend;
    this.ignorePatterns = [...DEFAULT_IGNORE_PATTERNS, ...(options?.ignorePatterns || [])];
  }

  public subscribe(rootSpan: RootSpan) {
    this.currentRootSpan = rootSpan;
    this.currentViewId = this.currentRootSpan?.spanContext().traceId;
    if (!this.listenersSet) {
      this.setupGlobalListeners();
      this.listenersSet = true;
    }
  }

  public unsubscribe() {
    this.flush("view_end");
    this.currentRootSpan = undefined;
    this.currentViewId = undefined;
    this.clearFlushTimer();
  }

  public flush(reason: FlushReason = "manual") {
    log("reason to flush", reason);
    if (this.queue.length === 0) {
      this.clearFlushTimer();
      return;
    }
    this.queue = [];
    this.clearFlushTimer();
  }

  public captureError(error: unknown, context: Record<string, any> = {}, source: ErrorSource) {
    if (!this.isSourceEnabled(source)) return;

    const contextCopy = { ...(context || {}) };

    let handled: boolean | undefined = undefined;
    if (typeof contextCopy.handled === "boolean") {
      handled = contextCopy.handled;
      delete contextCopy.handled;
    }

    const event = this.buildEvent(error, contextCopy, source, handled);
    if (!event) return;
    this.processEvent(event);
  }

  public captureNetworkError(error: unknown, context: Record<string, any> = {}) {
    if (!this.config.network) return;
    this.captureError(error, { ...(context || {}) }, "network");
  }

  public captureManualError(error: unknown, context: Record<string, any> = {}) {
    const contextCopy = { ...(context || {}) };
    const handled = typeof contextCopy.handled === "boolean" ? contextCopy.handled : true;
    if (typeof contextCopy.handled === "boolean") {
      delete contextCopy.handled;
    }
    const event = this.buildEvent(error, contextCopy, "manual", handled);
    if (!event) return;
    this.processEvent(event);
  }

  private processEvent(event: ErrorEventPayload, options: ProcessOptions = {}) {
    if (!options.skipIgnore && this.shouldIgnore(event)) return;

    const processed = options.skipBeforeSend ? event : this.applyBeforeSend(event);
    if (!processed) return;

    this.enqueue(processed);
  }

  private enqueue(event: ErrorEventPayload) {
    if (this.currentRootSpan) {
      this.currentRootSpan.addEvent("exception", event.attributes, event.timestamp);
      const message = String(event.attributes[CONV.EXCEPTION_MESSAGE] || "Error");
      const shouldMarkError = event.source !== "manual" || event.handled === false;
      if (shouldMarkError && typeof this.currentRootSpan.setStatus === "function") {
        this.currentRootSpan.setStatus({ code: SpanStatusCode.ERROR, message });
      }
      this.addErrorAttributesToSpan(event.attributes);
    }

    // Send error as a log in addition to trace
    try {
      const activeSpan = trace.getActiveSpan();
      const ctxAttrs: Record<string, string> = {};
      if (activeSpan && typeof activeSpan.spanContext === "function") {
        const sc = activeSpan.spanContext();
        if (sc?.traceId) ctxAttrs["trace.id"] = sc.traceId;
        if (sc?.spanId) ctxAttrs["span.id"] = sc.spanId;
      }

      const errorMessage = String(event.attributes[CONV.EXCEPTION_MESSAGE] || "Error");
      const errorType = event.attributes[CONV.EXCEPTION_TYPE]
        ? String(event.attributes[CONV.EXCEPTION_TYPE])
        : undefined;
      const errorStack = event.attributes[CONV.EXCEPTION_STACKTRACE]
        ? String(event.attributes[CONV.EXCEPTION_STACKTRACE])
        : undefined;

      forceError({
        message: errorMessage,
        attributes: {
          ...event.attributes,
          ...ctxAttrs,
          "event.type": "error",
          ...(errorType ? { "error.type": errorType } : {}),
          ...(errorStack ? { "error.stack": errorStack } : {}),
        },
      });
    } catch (err) {
      warn("[L9RUM ErrorCollector] Failed to emit error log", err);
    }

    this.queue.push(event);
    if (this.queue.length >= BATCH_SIZE) {
      this.flush("size");
      return;
    }
    this.scheduleFlush();
  }

  private scheduleFlush() {
    if (this.flushTimer || this.queue.length === 0) return;
    this.flushTimer = setTimeout(() => {
      this.flushTimer = undefined;
      this.flush("timer");
    }, BATCH_INTERVAL_MS);
  }

  private clearFlushTimer() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = undefined;
    }
  }

  private applyBeforeSend(event: ErrorEventPayload): ErrorEventPayload | null {
    if (!this.beforeSend) return event;
    try {
      const clone: ErrorEventPayload = {
        ...event,
        attributes: { ...event.attributes },
      };
      const result = this.beforeSend(clone);
      if (!result) return null;
      return {
        source: result.source ?? event.source,
        timestamp: result.timestamp ?? event.timestamp,
        handled: typeof result.handled === "boolean" ? result.handled : event.handled,
        attributes: { ...event.attributes, ...result.attributes },
      };
    } catch (err) {
      warn("[L9RUM ErrorCollector] beforeSend threw; dropping error", err);
      return null;
    }
  }

  private isSourceEnabled(source: ErrorSource) {
    switch (source) {
      case "console":
        return this.config.console;
      case "source":
        return this.config.global;
      case "report":
        return this.config.report;
      case "network":
        return this.config.network;
      case "manual":
        return true; // manual capture is always enabled
      default:
        return true;
    }
  }

  private buildEvent(
    error: unknown,
    context: Record<string, any>,
    source: ErrorSource,
    handled?: boolean,
  ): ErrorEventPayload | null {
    const timestamp = Date.now();
    const parsed = parseStack(error, source);
    const attributes: Attributes = {};

    const contextMessage = typeof context?.message === "string" ? context.message : undefined;
    const message = contextMessage || parsed.message || this.deriveFallbackMessage(error, source);

    if (parsed.name) attributes[CONV.EXCEPTION_TYPE] = parsed.name;
    if (message) attributes[CONV.EXCEPTION_MESSAGE] = message;
    if (parsed.stack) attributes[CONV.EXCEPTION_STACKTRACE] = parsed.stack;
    if (parsed.frames?.length)
      attributes[CONV.ERROR_STACK_FRAMES] = this.safeStringify(parsed.frames);
    if (parsed.file) attributes[CONV.ERROR_FILENAME] = parsed.file;
    if (typeof parsed.line === "number") attributes[CONV.ERROR_LINENO] = parsed.line;
    if (typeof parsed.column === "number") attributes[CONV.ERROR_COLNO] = parsed.column;
    if (typeof handled === "boolean") attributes[CONV.ERROR_HANDLED] = handled;

    attributes[CONV.ERROR_SOURCE] = source;

    const sessionId = getCurrentSessionId();
    const previousSessionId = getPreviousSessionId();
    if (sessionId) attributes[CONV.SESSION_ID] = sessionId;
    if (previousSessionId) attributes[CONV.SESSION_PREVIOUS_ID] = previousSessionId;
    if (this.currentViewId) attributes[CONV.VIEW_ID] = this.currentViewId;

    const sanitizedContext = this.normalizeContext(context);
    Object.entries(sanitizedContext).forEach(([key, value]) => {
      if (value !== undefined) {
        attributes[key] = value as any;
      }
    });

    if (!attributes[CONV.EXCEPTION_MESSAGE]) {
      attributes[CONV.EXCEPTION_MESSAGE] = this.deriveFallbackMessage(error, source);
    }

    return {
      source,
      timestamp,
      handled,
      attributes,
    };
  }

  private deriveFallbackMessage(error: unknown, source: ErrorSource) {
    if (error instanceof Error) return error.message || error.name || "Unknown error";
    if (typeof error === "string") return error;
    if (error && typeof error === "object") {
      const maybeMessage = (error as any).message;
      if (typeof maybeMessage === "string" && maybeMessage) return maybeMessage;
    }
    return `${source} error`;
  }

  private normalizeContext(context: Record<string, any> = {}) {
    const output: Record<string, any> = {};
    Object.keys(context || {}).forEach((key) => {
      if (key === "message" || key === "handled") return;
      const value = this.normalizeValue((context as any)[key]);
      if (value !== undefined) {
        output[key] = value;
      }
    });
    return output;
  }

  private normalizeValue(value: any, depth = 0): any {
    if (value === null || value === undefined) return undefined;
    if (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
      return value;
    if (typeof value === "bigint") return value.toString();
    if (value instanceof Date) return value.toISOString();
    if (value instanceof URL) return value.toString();
    if (value instanceof Error) return value.message || value.name || value.toString();
    if (value instanceof RegExp) return value.toString();
    if (typeof value === "function") return `[Function ${value.name || "anonymous"}]`;
    if (Array.isArray(value)) {
      if (depth > 2) return this.safeStringify(value);
      const arr = value
        .slice(0, 20)
        .map((item) => this.normalizeValue(item, depth + 1))
        .filter((item) => item !== undefined);
      return arr.length ? arr : undefined;
    }
    if (typeof value === "object") {
      return this.safeStringify(value);
    }
    try {
      return String(value);
    } catch {
      return undefined;
    }
  }

  private safeStringify(value: unknown) {
    const seen = new WeakSet();
    try {
      const stringified = JSON.stringify(value, function replacer(_key, val) {
        if (typeof val === "object" && val !== null) {
          if (seen.has(val)) return "[Circular]";
          seen.add(val);
        }
        if (typeof val === "function") {
          return `[Function ${val.name || "anonymous"}]`;
        }
        if (val instanceof Error) {
          return {
            name: val.name,
            message: val.message,
            stack: val.stack,
          };
        }
        if (typeof Event !== "undefined" && val instanceof Event) {
          return { type: val.type };
        }
        return val;
      });
      if (!stringified) return undefined;
      if (stringified.length > MAX_SERIALIZED_LENGTH) {
        return `${stringified.slice(0, MAX_SERIALIZED_LENGTH)}…`;
      }
      return stringified;
    } catch {
      try {
        return String(value);
      } catch {
        return undefined;
      }
    }
  }

  private shouldIgnore(event: ErrorEventPayload) {
    const message = String(event.attributes[CONV.EXCEPTION_MESSAGE] || "");
    if (!message) return false;
    if (event.source === "network") {
      const status = event.attributes[CONV.HTTP_STATUS_CODE];
      if (!status && /abort/i.test(message)) {
        return true;
      }
    }
    return this.ignorePatterns.some((pattern) => {
      if (typeof pattern === "string") {
        return message === pattern;
      }
      try {
        return pattern.test(message);
      } catch {
        return false;
      }
    });
  }

  private applyContextAttributes(attributes: Attributes) {
    const sessionId = getCurrentSessionId();
    const previousSessionId = getPreviousSessionId();
    if (sessionId) attributes[CONV.SESSION_ID] = sessionId;
    if (previousSessionId) attributes[CONV.SESSION_PREVIOUS_ID] = previousSessionId;
    if (this.currentViewId) attributes[CONV.VIEW_ID] = this.currentViewId;
  }

  private addErrorAttributesToSpan(attributes: Attributes) {
    if (!this.currentRootSpan) return;
    const keys: Array<[string, unknown]> = [
      [CONV.EXCEPTION_TYPE, attributes[CONV.EXCEPTION_TYPE]],
      [CONV.EXCEPTION_MESSAGE, attributes[CONV.EXCEPTION_MESSAGE]],
      [CONV.EXCEPTION_STACKTRACE, attributes[CONV.EXCEPTION_STACKTRACE]],
      [CONV.ERROR_SOURCE, attributes[CONV.ERROR_SOURCE]],
    ];
    keys.forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        this.currentRootSpan?.setAttribute(key, value as any);
      }
    });
  }

  private setupGlobalListeners() {
    if (typeof window === "undefined") return;

    if (this.config.console && !this.consolePatched) {
      try {
        const original = window.console.error;
        window.console.error = (...args: any[]) => {
          try {
            const primary = args[0] instanceof Error ? args[0] : args[0];
            this.captureError(primary, { args }, "console");
          } catch {}
          original.apply(window.console, args as any);
        };
        this.consolePatched = true;
      } catch (err) {
        warn("[L9RUM ErrorCollector] Failed to patch console.error", err);
      }
    }

    if (this.config.global) {
      try {
        window.addEventListener("error", (event: ErrorEvent) => {
          const payload = event?.error || event?.message || event;
          this.captureError(
            payload,
            { filename: event?.filename, lineno: event?.lineno, colno: event?.colno },
            "source",
          );
        });
      } catch (err) {
        warn("[L9RUM ErrorCollector] Failed to attach window.onerror", err);
      }

      try {
        window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
          this.captureError(event.reason, { promise: event.promise }, "source");
        });
      } catch (err) {
        warn("[L9RUM ErrorCollector] Failed to attach unhandledrejection listener", err);
      }
    }

    if (this.config.report && typeof window.ReportingObserver === "function") {
      try {
        const observer = new window.ReportingObserver(
          (reports: any[]) => {
            reports.forEach((report) => {
              const context = {
                report: {
                  type: report?.type,
                  url: report?.url,
                  body: report?.body,
                },
              };
              this.captureError(report, context, "report");
            });
          },
          { types: ["crash", "deprecation", "intervention"], buffered: true },
        );
        observer.observe();
      } catch (err) {
        warn("[L9RUM ErrorCollector] Failed to start ReportingObserver", err);
      }
    }
  }
}
